import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100 shadow-sm">
      {/* Yellow accent bar at top */}
      <div className="h-1 bg-yellow-500" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <img
              src={companyInfo.logo}
              alt="Barchalo Logo"
              className="h-10 sm:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8 ml-auto mr-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-semibold tracking-wide uppercase transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-yellow-600'
                    : 'text-slate-700 hover:text-yellow-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 text-slate-600 hover:text-yellow-600 transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">{companyInfo.phone}</span>
            </a>
            <Link
              to="/contact"
              data-testid="header-cta-btn"
              className="px-6 py-2.5 bg-yellow-500 text-slate-900 font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-yellow-400 transition-all duration-300 shadow-sm hover:shadow-md"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button - 44x44 tap target */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden -mr-2 w-11 h-11 flex items-center justify-center text-slate-700 hover:text-yellow-600 transition-colors active:scale-95"
            data-testid="mobile-menu-btn"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-40"
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />
          
          {/* Menu window */}
          <div className="md:hidden fixed top-[68px] right-3 left-3 sm:left-auto sm:w-72 z-50 rounded-sm shadow-2xl overflow-hidden border border-slate-200 bg-white max-h-[calc(100vh-80px)] overflow-y-auto">
            <nav className="flex flex-col p-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`text-base font-semibold py-3.5 px-4 rounded-sm transition-colors duration-300 uppercase tracking-wide min-h-[44px] flex items-center ${
                    isActive(link.path)
                      ? 'text-yellow-600 bg-yellow-50'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-yellow-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-slate-100 my-2" />
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 text-slate-600 hover:text-yellow-600 transition-colors duration-300 py-3.5 px-4 rounded-sm min-h-[44px]"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium text-sm">{companyInfo.phone}</span>
              </a>
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="text-center py-3.5 px-4 bg-yellow-500 text-slate-900 font-bold uppercase tracking-wide rounded-sm hover:bg-yellow-400 transition-colors duration-300 mt-2 min-h-[44px] flex items-center justify-center"
              >
                Get a Quote
              </Link>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
